import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ContractGatePrompt from '../components/ContractGatePrompt';
import {
  CONTRACT_GATE_MESSAGE,
  requiresContractForPublishing,
} from '../lib/contractGate';

interface Work {
  id: string;
  title: string;
}

export default function CreateCampaign() {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [works, setWorks] = useState<Work[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalSlices, setGoalSlices] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedWorkId, setSelectedWorkId] = useState<string>('none');

  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!publisher?.id) return;
    supabase
      .from('works')
      .select('id, title')
      .eq('publisher_id', publisher.id)
      .neq('status', 'archived')
      .order('title')
      .then(({ data }: { data: Work[] | null }) => {
        if (data) setWorks(data);
      });
  }, [publisher?.id]);

  const contractBlocked = requiresContractForPublishing(publisher);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publisher?.id) return;
    if (contractBlocked) {
      toast.error(CONTRACT_GATE_MESSAGE);
      return;
    }
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!description.trim()) { toast.error('Description is required'); return; }
    const goal = parseInt(goalSlices);
    if (!goal || goal < 100) { toast.error('Goal must be at least 100 slices'); return; }
    if (!endDate || endDate <= todayStr) { toast.error('End date must be in the future'); return; }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('crowdfunding_campaigns').insert({
        publisher_id: publisher.id,
        title: title.trim(),
        description: description.trim(),
        goal_slices: goal,
        total_slices_raised: 0,
        backer_count: 0,
        status: 'draft',
        end_date: endDate,
        work_id: selectedWorkId === 'none' ? null : selectedWorkId,
      });
      if (error) throw error;
      toast.success('Campaign created!');
      navigate('/campaigns');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold">New Campaign</h1>
          <p className="text-muted-foreground mt-1">Create a crowdfunding campaign for your project</p>
        </div>
      </div>

      {contractBlocked && <ContractGatePrompt />}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="partner-card">
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
            <CardDescription>Tell backers about your project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Campaign Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="My amazing film project"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description / Pitch *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Tell potential backers about your project, why it matters, and what the funding will be used for..."
                rows={5}
                className="resize-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goal">Goal (slices) *</Label>
                <Input
                  id="goal"
                  type="number"
                  min="100"
                  value={goalSlices}
                  onChange={e => setGoalSlices(e.target.value)}
                  placeholder="10000"
                  required
                />
                <p className="text-xs text-muted-foreground">Minimum 100 slices</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date *</Label>
                <Input
                  id="endDate"
                  type="date"
                  min={todayStr}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Linked Work (optional)</Label>
              <Select value={selectedWorkId} onValueChange={setSelectedWorkId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a work (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked work</SelectItem>
                  {works.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optionally link this campaign to an existing work in your library
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/campaigns')}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || contractBlocked}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Campaign'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
